import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Loader from "../components/common/Loader";
import ErrorState from "../components/common/ErrorState";
import EmptyState from "../components/common/EmptyState";
import Badge from "../components/common/Badge";
import Pagination from "../components/common/Pagination";
import Modal from "../components/common/Modal";
import universityService from "../services/universityService";
import { formatDate } from "../utils/format";

const EMPTY_FORM = { name: "", location: "", type_of_hire: "", start_date: "", end_date: "" };

export default function Universities() {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  function load() {
    setState("loading");
    universityService
      .list({ search, page, limit: 10 })
      .then((res) => {
        setRows(res.data || []);
        setPagination(res.pagination || { totalPages: 1 });
        setState((res.data || []).length ? "ready" : "empty");
      })
      .catch(() => setState("error"));
  }

  useEffect(load, [page]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setModalOpen(true);
  }

  function openEdit(u) {
    setEditing(u);
    setForm({
      name: u.name || "",
      location: u.location || "",
      type_of_hire: u.type_of_hire || "",
      start_date: u.start_date ? u.start_date.slice(0, 10) : "",
      end_date: u.end_date ? u.end_date.slice(0, 10) : "",
    });
    setFormError("");
    setModalOpen(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setFormError("");
    try {
      if (editing) {
        await universityService.update(editing.id, form);
      } else {
        await universityService.create(form);
      }
      setModalOpen(false);
      load();
    } catch (err) {
      setFormError(err.friendlyMessage || "Could not save campus.");
    } finally {
      setSaving(false);
    }
  }

  // NOTE: university_details.is_active is stored as an ENUM('1','0') column,
  // so MySQL always returns it as the STRING "1" or "0" — never a real
  // number. In JavaScript, !!"0" is true (any non-empty string is truthy),
  // so comparisons must check the actual value with Number(...) === 1
  // instead of relying on truthiness.
  async function toggleStatus(u) {
    try {
      await universityService.setStatus(u.id, Number(u.is_active) === 1 ? 0 : 1);
      load();
    } catch (_) {}
  }

  return (
    <div className="pm-page">
      <header className="pm-page__header">
        <div>
          <span className="pm-eyebrow">Campus management</span>
          <h1>Universities</h1>
        </div>
        <button className="pm-btn pm-btn--primary" onClick={openCreate}>+ Add campus</button>
      </header>

      <div className="pm-toolbar">
        <input
          className="pm-search"
          placeholder="Search by name or location…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {state === "loading" && <Loader label="Loading universities" />}
      {state === "error" && <ErrorState onRetry={load} />}
      {state === "empty" && <EmptyState title="No campuses found" description="Add your first campus to get started." />}

      {(state === "ready") && (
        <>
          <div className="pm-campus-manage-grid">
            {rows.map((u, i) => (
              <motion.div
                className="pm-manage-card"
                key={u.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: Math.min(i, 6) * 0.04 }}
              >
                <div className="pm-manage-card__top">
                  <Badge active={Number(u.is_active) === 1} />
                  <span className="pm-manage-card__count">{u.student_count} applicants</span>
                </div>
                <h3><Link to={`/admin/universities/${u.id}`}>{u.name}</Link></h3>
                <p>{u.location || "Location not set"}</p>
                <div className="pm-manage-card__meta">
                  <span>{u.type_of_hire || "Hiring type not set"}</span>
                  <span>{formatDate(u.start_date)} – {formatDate(u.end_date)}</span>
                </div>
                <div className="pm-manage-card__actions">
                  <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => openEdit(u)}>Edit</button>
                  <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => toggleStatus(u)}>
                    {Number(u.is_active) === 1 ? "Deactivate" : "Activate"}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
          <Pagination page={page} totalPages={pagination.totalPages} onChange={setPage} />
        </>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit campus" : "Add campus"}>
        <form onSubmit={handleSave} className="pm-modal-form">
          <div className="pm-field">
            <label>Name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="pm-field">
            <label>Location</label>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div className="pm-field">
            <label>Hiring type</label>
            <input value={form.type_of_hire} onChange={(e) => setForm({ ...form, type_of_hire: e.target.value })} placeholder="e.g. Full-time, Internship" />
          </div>
          <div className="pm-field-grid">
            <div className="pm-field">
              <label>Start date</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="pm-field">
              <label>End date</label>
              <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          {formError && <div className="pm-form-error">{formError}</div>}
          <button className="pm-btn pm-btn--primary" disabled={saving} type="submit">
            {saving ? "Saving…" : "Save campus"}
          </button>
        </form>
      </Modal>
    </div>
  );
}